// FILE: src/components/ReportModal.js
//
// Overlay modal that lets learners or SMEs report a question for review.
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   question          {object}   — normalized question object (id, referenceId,
//                                  question text, examId if needed)
//   examId            {string}   — "pmp" | "cpmai"
//   selectedAnswers   {string[]} — choice ids the learner had selected (optional)
//   onSubmit          {function} — called with the report payload object when
//                                  the form is submitted; the modal stays open
//                                  and transitions to a confirmation screen
//   onClose           {function} — called when the user dismisses the modal
//                                  (Cancel, × close, or Done after confirmation)
//
// ── Internal flow ─────────────────────────────────────────────────────────────
//
//   Form state (submitted=false):
//     category (required) + comments (required) + reporter (optional)
//     Submit button disabled until both required fields are filled.
//     Clicking Submit calls onSubmit(payload) and sets submitted=true.
//
//   Confirmation state (submitted=true):
//     Shows a success message and a "Done" button that calls onClose().

import React, { useState } from 'react';
import { REPORT_CATEGORIES } from '../../services/reportService';

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  // Fixed overlay dims the page behind the modal.
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '16px',
  },
  modal: {
    background: '#fff',
    borderRadius: '10px',
    border: '2px solid #333',
    width: '100%',
    maxWidth: '540px',
    maxHeight: '90vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #eee',
    gap: '12px',
  },
  modalTitle: {
    fontWeight: 'bold',
    fontSize: '1.05rem',
    color: '#222',
    flex: 1,
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    fontSize: '1.3rem',
    cursor: 'pointer',
    color: '#888',
    lineHeight: 1,
    padding: '2px 6px',
    borderRadius: '4px',
  },
  modalBody: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  // Compact question info block at the top of the form.
  questionInfo: {
    background: '#f8f8f8',
    border: '1px solid #e8e8e8',
    borderRadius: '6px',
    padding: '12px 14px',
  },
  questionRefId: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.73rem',
    color: '#888',
    background: '#eee',
    border: '1px solid #ddd',
    borderRadius: '3px',
    padding: '1px 6px',
    display: 'inline-block',
    marginBottom: '6px',
  },
  questionPreviewText: {
    fontSize: '0.9rem',
    color: '#444',
    lineHeight: '1.45',
    margin: 0,
    // Clamp to 3 lines so very long questions don't blow up the modal
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '0.87rem',
    fontWeight: '600',
    color: '#444',
  },
  required: {
    color: '#c62828',
    marginLeft: '2px',
  },
  select: {
    padding: '9px 12px',
    fontSize: '0.95rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    background: '#fff',
    color: '#333',
    width: '100%',
    cursor: 'pointer',
  },
  textarea: {
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    resize: 'vertical',
    minHeight: '90px',
    fontFamily: 'inherit',
    lineHeight: '1.5',
    color: '#333',
    width: '100%',
    boxSizing: 'border-box',
  },
  input: {
    padding: '9px 12px',
    fontSize: '0.95rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    width: '100%',
    boxSizing: 'border-box',
    color: '#333',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '14px 20px',
    borderTop: '1px solid #eee',
  },
  cancelButton: {
    padding: '10px 22px',
    background: 'transparent',
    border: '1px solid #bbb',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: '#555',
  },
  submitButton: (disabled) => ({
    padding: '10px 22px',
    background: disabled ? '#ccc' : '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.95rem',
    fontWeight: 'bold',
    opacity: disabled ? 0.7 : 1,
    transition: 'background 0.12s',
  }),

  // ── Confirmation state styles ────────────────────────────────────────────────
  confirmation: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 28px',
    textAlign: 'center',
    gap: '12px',
  },
  confirmIcon: {
    fontSize: '2.8rem',
    lineHeight: 1,
    color: '#2e7d32',
  },
  confirmHeading: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#222',
    margin: 0,
  },
  confirmBody: {
    fontSize: '0.93rem',
    color: '#555',
    lineHeight: '1.6',
    margin: 0,
  },
  confirmRefId: {
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.8rem',
    color: '#888',
    background: '#f0f0f0',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '3px 10px',
  },
  doneButton: {
    marginTop: '8px',
    padding: '10px 32px',
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 'bold',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function ReportModal({
  question,
  examId,
  selectedAnswers = [],
  onSubmit,
  onClose,
}) {
  const [category, setCategory]   = useState('');
  const [comments, setComments]   = useState('');
  const [reporter, setReporter]   = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!question) return null;

  const canSubmit = category.length > 0 && comments.trim().length > 0;

  function handleSubmit() {
    if (!canSubmit) return;
    onSubmit({
      questionId:       question.id,
      referenceId:      question.referenceId ?? '',
      examId,
      category,
      comments,
      questionSnapshot: question.question,
      selectedAnswers:  [...selectedAnswers],
      reporter,
    });
    setSubmitted(true);
  }

  // ── Confirmation screen (after submit) ───────────────────────────────────────
  if (submitted) {
    return (
      <div data-testid="report-modal" style={styles.overlay}>
        <div style={styles.modal}>
          <div data-testid="report-confirmation" style={styles.confirmation}>
            <div style={styles.confirmIcon}>✓</div>
            <h3 style={styles.confirmHeading}>Report Submitted</h3>
            <p style={styles.confirmBody}>
              Thank you — an SME will review this question.
            </p>
            {question.referenceId && (
              <span style={styles.confirmRefId}>{question.referenceId}</span>
            )}
            <button
              data-testid="report-done-button"
              style={styles.doneButton}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Report form ──────────────────────────────────────────────────────────────
  return (
    <div data-testid="report-modal" style={styles.overlay}>
      <div style={styles.modal}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div style={styles.modalHeader}>
          <span style={styles.modalTitle}>⚑ Report Question</span>
          <button
            data-testid="report-close-button"
            style={styles.closeButton}
            aria-label="Close report modal"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div style={styles.modalBody}>

          {/* Question info — auto-filled, read-only */}
          <div style={styles.questionInfo}>
            {question.referenceId && (
              <span style={styles.questionRefId}>{question.referenceId}</span>
            )}
            <p style={styles.questionPreviewText}>{question.question}</p>
          </div>

          {/* Category — required */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="report-category">
              Category <span style={styles.required}>*</span>
            </label>
            <select
              id="report-category"
              data-testid="report-category-select"
              style={styles.select}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Select a category…</option>
              {REPORT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Comments — required */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="report-comments">
              Comments <span style={styles.required}>*</span>
            </label>
            <textarea
              id="report-comments"
              data-testid="report-comments-input"
              style={styles.textarea}
              placeholder="Describe the issue in detail…"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          {/* Reporter name — optional */}
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="report-reporter">
              Your name <span style={{ color: '#aaa', fontWeight: 'normal' }}>(optional)</span>
            </label>
            <input
              id="report-reporter"
              data-testid="report-reporter-input"
              type="text"
              style={styles.input}
              placeholder="e.g. Jane Smith"
              value={reporter}
              onChange={(e) => setReporter(e.target.value)}
            />
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div style={styles.modalFooter}>
          <button
            data-testid="report-cancel-button"
            style={styles.cancelButton}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            data-testid="report-submit-button"
            style={styles.submitButton(!canSubmit)}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Submit Report
          </button>
        </div>

      </div>
    </div>
  );
}
